import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';
import { supabase, getUserProfile } from '../lib/supabase';
import PasswordInput from '../components/PasswordInput';
import './SignIn.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * First-login forced password change.
 *
 * Reached by AppShell when it detects `user_profiles.must_change_password
 * = true` on the signed-in user's profile — which is set on every
 * account provisioned by a master admin (Full mode onboarding,
 * "Resend credentials" action). No back door: signing out from here
 * and signing back in bounces the user right back.
 *
 * On success, hits `POST /api/auth/mark-password-changed` to clear the
 * flag, then routes the user to their home landing page.
 */
const ChangePassword = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/signin'); return; }
      const { data } = await getUserProfile(session.user.id);
      if (!data) { navigate('/signin'); return; }
      // Safety net — if the flag is already cleared, don't trap the user here.
      if (!data.must_change_password) {
        navigate(data.role === 'barber' ? '/diary'
                : data.role === 'master_admin' ? '/admin/dashboard'
                : '/dashboard');
        return;
      }
      setProfile(data);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError("Passwords don't match.");
    setSaving(true);
    try {
      const { error: upErr } = await supabase.auth.updateUser({ password });
      if (upErr) throw upErr;

      // Clear the flag server-side.
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/mark-password-changed`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      if (!res.ok) throw new Error(`Server rejected the update (${res.status})`);

      setDone(true);
      // Sign out so the next sign-in is with the new password — the
      // strictest "log in with new password" experience.
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/signin');
      }, 1600);
    } catch (err) {
      setError(err.message || 'Could not update your password.');
    } finally {
      setSaving(false);
    }
  };

  const signOutInstead = async () => {
    await supabase.auth.signOut();
    navigate('/signin');
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>;
  }

  return (
    <div className="auth" data-testid="change-password">
      <div className="auth-l">
        <div className="streak" style={{ top: '30%', right: '-20px', width: '240px' }}></div>
        <div className="streak" style={{ top: 'calc(30% + 12px)', right: '-20px', width: '160px', opacity: '0.5' }}></div>
        <div className="brandrow">
          <img className="mark" src="/logos/instacut-mark-28.svg" alt="" width="28" height="28" />
          Instacut
        </div>
        <h3>Choose a<br />password only you know.</h3>
        <p>You&apos;re signed in with a temporary password chosen by an admin. For your security, please set a new one before you can use Instacut.</p>
        <div className="quote">&ldquo;Pick something unique — a password manager makes it painless.&rdquo;</div>
      </div>

      <div className="auth-r">
        <h4>
          <Lock size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--acc)' }} />
          Set a new password
        </h4>
        <div className="s">
          Signed in as <b>{profile?.full_name || 'you'}</b>. You can&apos;t go anywhere else until this is done.
        </div>

        {done ? (
          <div
            className="error-message"
            style={{ background: 'var(--paper)', borderColor: 'var(--acc)', color: 'var(--ink)', display: 'flex', alignItems: 'flex-start', gap: 10 }}
            data-testid="change-password-done"
          >
            <Check size={18} color="var(--acc)" strokeWidth={3} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Password updated.</strong>
              <div style={{ marginTop: 4, color: 'var(--mute)' }}>
                Redirecting you to sign in with your new password&hellip;
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            {error && (
              <div className="error-message" data-testid="change-password-error">{error}</div>
            )}

            <div className="field">
              <label>New password</label>
              <PasswordInput
                required
                autoFocus
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={saving}
                placeholder="At least 8 characters"
                data-testid="cp-pw"
                toggleTestId="cp-pw-toggle"
                showStrength
                strengthTestIdPrefix="cp-pw"
              />
            </div>

            <div className="field">
              <label>Confirm password</label>
              <PasswordInput
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={saving}
                placeholder="Type it again"
                data-testid="cp-confirm"
                toggleTestId="cp-confirm-toggle"
              />
            </div>

            <button
              type="submit"
              className="btn p"
              disabled={saving || !password || !confirm}
              style={{ width: '100%', justifyContent: 'center', padding: '12px' }}
              data-testid="cp-submit"
            >
              {saving ? 'Updating…' : 'Update password and sign in'}
            </button>

            <div style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--mute)', marginTop: '18px' }}>
              Wrong account?{' '}
              <button
                type="button"
                onClick={signOutInstead}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--acc)', fontWeight: 600, cursor: 'pointer' }}
                data-testid="cp-signout"
              >
                Sign out
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;
